import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Printer } from 'lucide-react';
import { ErrorState } from '@/components/common/ErrorState';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatNumber, formatTaka } from '@/utils/number';
import { PCMP_PERMISSION } from '../api/pcmpAccess.api';
import {
  downloadPcmpPerformanceReportXlsx,
  pcmpPerformanceReportQueryOptions,
} from '../api/pcmpReports.api';
import { PcmpPerformanceInsights } from '../components/PcmpReports/PcmpPerformanceInsights';
import { PcmpPerformanceReportTable } from '../components/PcmpReports/PcmpPerformanceReportTable';
import { PcmpExportButton } from '../components/PcmpQueue/PcmpExportButton';
import { PcmpReportFilterBar } from '../components/PcmpReports/PcmpReportFilterBar';
import { usePcmpPerformanceReportFilters } from '../hooks/usePcmpPerformanceReportFilters';
import { usePcmpPermissions } from '../hooks/usePcmpPermissions';

// A large amount must not run under the next card (LDS-802): the grid only goes
// six across where they fit, and a figure that still does not fit is ellipsed.
function Metric({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-xl border bg-card px-4 py-3 shadow-sm">
      {value === undefined ? (
        <span className="h-6 w-24 animate-pulse rounded bg-muted" aria-hidden />
      ) : (
        <span className="truncate text-lg font-semibold tabular-nums" title={value}>
          {value}
        </span>
      )}
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

/** Sales target, CMP and disbursement by division and region (LDS-740). */
export function PcmpPerformanceReportPage() {
  const controller = usePcmpPerformanceReportFilters();
  const { appliedFilters } = controller;
  const { can } = usePcmpPermissions();
  const canExport = can(PCMP_PERMISSION.VIEW_ALL, PCMP_PERMISSION.EXPORT);

  const { data, isFetching, error, refetch } = useQuery(
    pcmpPerformanceReportQueryOptions(appliedFilters),
  );

  const total = data?.grandTotal;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Performance Report</h1>
          <p className="text-sm text-muted-foreground">
            Sales target, CMP, disbursement and what is still processing or due, by division and
            region.
          </p>
        </div>
        {canExport ? (
          <div className="flex flex-wrap items-center gap-2">
            <PcmpExportButton
              disabled={!data}
              download={(date) => downloadPcmpPerformanceReportXlsx(appliedFilters, date)}
            />
            <Link
              to="/pcmp/reports/performance/print"
              search={appliedFilters}
              target="_blank"
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
            >
              <Printer className="size-3.5" />
              Print / PDF
            </Link>
          </div>
        ) : null}
      </div>

      <PcmpReportFilterBar controller={controller} idPrefix="pcmp-performance" />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 2xl:grid-cols-6">
        <Metric label="Institutes" value={total && formatNumber(total.instituteCount)} />
        <Metric label="Sales Target" value={total && formatTaka(total.salesTarget)} />
        <Metric label="CMP Amount" value={total && formatTaka(total.cmpAmount)} />
        <Metric label="Disbursed" value={total && formatTaka(total.disbursedAmount)} />
        <Metric label="Processing" value={total && formatTaka(total.processingAmount)} />
        <Metric label="Due" value={total && formatTaka(total.due)} />
      </div>

      {data ? <PcmpPerformanceInsights highest={data.highest} lowest={data.lowest} /> : null}

      {error && !data ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the Performance report"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      ) : (
        <div className="flex flex-col gap-2 rounded-xl border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
            <span className="font-medium">{data?.periodLabel ?? 'Loading…'}</span>
            <span className="text-xs text-muted-foreground">
              Counts every proposal that is not rejected, whatever the project&apos;s window.
            </span>
          </div>
          {data ? (
            <div
              className={cn('overflow-x-auto', isFetching && 'opacity-60 transition-opacity')}
              aria-busy={isFetching}
            >
              <PcmpPerformanceReportTable report={data} />
            </div>
          ) : (
            <div className="h-48 animate-pulse rounded-md bg-muted" aria-hidden />
          )}
        </div>
      )}
    </div>
  );
}
